// App.js
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Fab, useScrollTrigger, Zoom } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Header from './components/Header';
import Footer from './components/Footer';
import LoadingSkeleton from './components/LoadingSkeleton';
import Cancellation from './pages/Cancellation';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ShippingDeliveryPolicy from './pages/ShippingDeliveryPolicy';
import TermsAndConditions from './pages/TermsAndConditions';

// Lazy-loaded components
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const IndustrialCatering = lazy(() => import('./pages/services/IndustrialCatering'));
const EducationalCatering = lazy(() => import('./pages/services/EducationalCatering'));
const HealthcareCatering = lazy(() => import('./pages/services/HealthcareCatering'));
const RetailFoodServices = lazy(() => import('./pages/services/RetailFoodServices'));
const Gallery = lazy(() => import('./pages/services/Gallery'));
const Clients = lazy(() => import('./pages/Clients'));
const CSR = lazy(() => import('./pages/CSR'));
const Careers = lazy(() => import('./pages/Careers'));
const Contact = lazy(() => import('./pages/Contact'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Install = lazy(() => import('./pages/Install'));

const theme = createTheme({
  palette: {
    primary: {
      main: '#e53935', // Red color
    },
    secondary: {
      main: '#ff8a65', // Orange color
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
  },
});

// Back to Top component
function ScrollTop(props) {
  const { children } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = (event) => {
    const anchor = (event.target.ownerDocument || document).querySelector(
      '#back-to-top-anchor',
    );

    if (anchor) {
      anchor.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  return (
    <Zoom in={trigger}>
      <div
        onClick={handleClick}
        role="presentation"
        style={{
          position: 'fixed',
          bottom: '32px',
          left: '32px', // Changed from right to left
          zIndex: 1000,
        }}
      >
        {children}
      </div>
    </Zoom>
  );
}

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <div id="back-to-top-anchor" />
        <Header />
        
        <Suspense fallback={<LoadingSkeleton />}>
          <Routes>
            <Route path="/" element={loading ? <LoadingSkeleton /> : <Home />} />
            <Route path="/about" element={loading ? <LoadingSkeleton /> : <About />} />
            
            {/* Services Routes */}
            <Route path="/services" element={loading ? <LoadingSkeleton /> : <Services />} />
            <Route path="/services/industrial" element={loading ? <LoadingSkeleton /> : <IndustrialCatering />} />
            <Route path="/services/educational" element={loading ? <LoadingSkeleton /> : <EducationalCatering />} />
            <Route path="/services/healthcare" element={loading ? <LoadingSkeleton /> : <HealthcareCatering />} />
            <Route path="/services/retail" element={loading ? <LoadingSkeleton /> : <RetailFoodServices />} />
            <Route path="/services/gallery" element={loading ? <LoadingSkeleton /> : <Gallery />} />
            <Route path="/install" element={loading ? <LoadingSkeleton /> : <Install />} />
            <Route path="/clients" element={loading ? <LoadingSkeleton /> : <Clients />} />
            <Route path="/csr" element={loading ? <LoadingSkeleton /> : <CSR />} />
            <Route path="/careers" element={loading ? <LoadingSkeleton /> : <Careers />} />
            <Route path="/contact" element={loading ? <LoadingSkeleton /> : <Contact />} />
            <Route path="/cancellation-and-refund" element={loading ? <LoadingSkeleton /> : <Cancellation />} />
            <Route path="/privacy-policy" element={loading ? <LoadingSkeleton /> : <PrivacyPolicy />} />
            <Route path="/shipping-and-delivery" element={loading ? <LoadingSkeleton /> : <ShippingDeliveryPolicy />} />
            <Route path="/terms-and-conditions" element={loading ? <LoadingSkeleton /> : <TermsAndConditions />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        
        <Footer />
        
        <ScrollTop>
  <Fab 
    color="primary" 
    size="medium" // Changed from small to medium
    aria-label="scroll back to top"
    sx={{
      zIndex:2000,
      boxShadow: 3, // Adds a subtle shadow
      '&:hover': {
        transform: 'scale(1.1)', // Slight scale on hover
      }
    }}
  >
    <KeyboardArrowUpIcon fontSize="large" />
  </Fab>
</ScrollTop>
      </Router>
    </ThemeProvider>
  );
}

export default App;